/**
 * Formato mixto: qué aspectos del documento toma la plantilla y cuáles se
 * quedan como en el original. Gemelo de
 * `processing-ms/src/format-templates/aspectos.util.ts`: misma semántica y
 * mismos casos (`aspectos.vectores.json`, idéntico byte a byte en los dos
 * repos).
 *
 * `null` = formato completo, lo de siempre. La fase 2 sabe tomar de la
 * plantilla la hoja, la orientación, los márgenes, el encabezado y el pie;
 * la fase 3 añade los guiones de relleno y la fórmula de cierre
 * (`ASPECTOS_DISPONIBLES`). La letra y las tablas de la plantilla solo van
 * en bloque (`GRUPO_TIPOGRAFIA`, motor de reflujo, «Personalizado»). La
 * validación de verdad la hace el backend; aquí solo lo que
 * necesita la interfaz.
 */

export const ASPECTOS = ['hoja', 'orientacion', 'margenes', 'encabezado', 'pie', 'tipografia', 'tablas', 'guiones', 'cierre'] as const;
export type Aspecto = (typeof ASPECTOS)[number];
export type OrigenAspecto = 'plantilla' | 'original';
export type AspectosFormato = { v: 1 } & Record<Aspecto, OrigenAspecto>;

/** Atajo «Solo encabezado y pie» (fase 1). */
export const SOLO_BANDAS: AspectosFormato = {
  v: 1, hoja: 'original', orientacion: 'original', margenes: 'original', encabezado: 'plantilla', pie: 'plantilla',
  tipografia: 'original', tablas: 'original', guiones: 'original', cierre: 'original',
};
/** Atajo «Encabezado, pie y hoja» (fase 2): bandas, hoja, orientación y márgenes de la plantilla. */
export const BANDAS_Y_HOJA: AspectosFormato = {
  v: 1, hoja: 'plantilla', orientacion: 'plantilla', margenes: 'plantilla', encabezado: 'plantilla', pie: 'plantilla',
  tipografia: 'original', tablas: 'original', guiones: 'original', cierre: 'original',
};
/** Se añaden al final: los vectores de antes siguen valiendo. */
export const ATAJOS = ['completo', 'soloBandas', 'bandasYHoja'] as const;
export type Atajo = (typeof ATAJOS)[number];
/** El orden en que se pintan las tarjetas (y el que recorren las flechas). */
export const ORDEN_ATAJOS_UI: readonly Atajo[] = ['soloBandas', 'bandasYHoja', 'completo'];

/**
 * Lo que el motor sabe tomar de la plantilla. El resto (letra y tablas, por
 * ahora) va siempre del original. Fase 3 (8 oct): se añaden los guiones de
 * relleno y la fórmula de cierre.
 */
export const ASPECTOS_DISPONIBLES: readonly Aspecto[] = ['hoja', 'orientacion', 'margenes', 'encabezado', 'pie', 'guiones', 'cierre'];

/** Fila ilegible: todo del original (sale la Opción 2, nunca el formato completo). */
export const ASPECTOS_ILEGIBLES: AspectosFormato = {
  v: 1, hoja: 'original', orientacion: 'original', margenes: 'original', encabezado: 'original', pie: 'original',
  tipografia: 'original', tablas: 'original', guiones: 'original', cierre: 'original',
};

/** Mapa de candados (como la cotización). Lo que no está aquí no bloquea ningún aspecto. */
export const CANDADO_A_ASPECTO: Record<string, Aspecto> = {
  marginTopMm: 'margenes', marginBottomMm: 'margenes', marginLeftMm: 'margenes', marginRightMm: 'margenes',
  headerImage: 'encabezado', footerImage: 'pie',
  lineHeight: 'tipografia', textAlign: 'tipografia',
  fillShortLines: 'guiones',
  closingKeepTogether: 'cierre',
};

const esAspecto = (x: unknown): x is Aspecto => typeof x === 'string' && (ASPECTOS as readonly string[]).includes(x);
const esObjetoPlano = (x: unknown): x is Record<string, unknown> =>
  !!x && typeof x === 'object' && !Array.isArray(x) && Object.getPrototypeOf(x) === Object.prototype;

/**
 * Lee unos aspectos. Lo que falta vale `'plantilla'`, salvo la orientación,
 * que si falta HEREDA el valor (ya resuelto) de la hoja: las plantillas
 * guardadas por la fase 1 no la traen. Todo `'plantilla'` = `null` (completo).
 */
export function normalizarAspectos(x: unknown): { ok: true; valor: AspectosFormato | null } | { ok: false; message: string } {
  if (x === null || x === undefined) return { ok: true, valor: null };
  if (!esObjetoPlano(x)) return { ok: false, message: 'Aspectos inválidos: se esperaba un objeto' };
  for (const clave of Object.keys(x)) {
    if (clave === 'v') {
      if (x.v !== 1) return { ok: false, message: 'Aspectos inválidos: versión desconocida' };
      continue;
    }
    if (!esAspecto(clave)) return { ok: false, message: `Aspectos inválidos: «${clave}» no es un aspecto` };
    if (x[clave] !== 'plantilla' && x[clave] !== 'original') {
      return { ok: false, message: `Aspectos inválidos: «${clave}» debe ser «plantilla» u «original»` };
    }
  }
  const valor = { v: 1 } as AspectosFormato;
  for (const a of ASPECTOS) {
    const dado = x[a] as OrigenAspecto | undefined;
    valor[a] = dado ?? (a === 'orientacion' ? valor.hoja : 'plantilla');
  }
  return ASPECTOS.every((a) => valor[a] === 'plantilla') ? { ok: true, valor: null } : { ok: true, valor };
}

/** Lo que guarda y sirve el backend: `orientacion` puede faltar (= la de la hoja). */
export type AspectosGuardados = Omit<AspectosFormato, 'orientacion'> & { orientacion?: OrigenAspecto };

/**
 * Lo que se MANDA al backend (y lo que él guarda y sirve): sin `orientacion`
 * cuando vale lo mismo que `hoja`. Gemelo de compactarAspectos de
 * processing-ms (mismos vectores). Un backend de la fase 1, que rechaza las
 * claves que no conoce, sigue aceptando «Solo encabezado y pie» y
 * «Encabezado, pie y hoja» si el front se publica antes que el backend o el
 * backend vuelve atrás; los lectores nuevos la heredan de la hoja.
 */
export function compactarAspectos(a: AspectosFormato | null): AspectosGuardados | null {
  if (!a) return null;
  const { orientacion, ...resto } = a;
  return orientacion === a.hoja ? resto : { ...a };
}

/** Un cuerpo (guardar, vista previa) con su `aspectos` compacto; lo ilegible o ausente, tal cual. */
export function cuerpoConAspectosCompactos<T extends { aspectos?: unknown }>(cuerpo: T): T {
  if (cuerpo.aspectos == null) return cuerpo;
  const n = normalizarAspectos(cuerpo.aspectos);
  return n.ok ? { ...cuerpo, aspectos: compactarAspectos(n.valor) } : cuerpo;
}

function iguales(a: AspectosFormato | null, b: AspectosFormato | null): boolean {
  if (a === null || b === null) return a === b;
  return ASPECTOS.every((k) => a[k] === b[k]);
}

export function atajoDe(a: unknown): Atajo | 'otro' {
  const n = normalizarAspectos(a);
  if (!n.ok) return 'otro';
  if (n.valor === null) return 'completo';
  if (iguales(n.valor, SOLO_BANDAS)) return 'soloBandas';
  return iguales(n.valor, BANDAS_Y_HOJA) ? 'bandasYHoja' : 'otro';
}

export function aspectosDeAtajo(atajo: Atajo): AspectosFormato | null {
  if (atajo === 'soloBandas') return { ...SOLO_BANDAS };
  if (atajo === 'bandasYHoja') return { ...BANDAS_Y_HOJA };
  return null;
}

/**
 * «Personalizado»: la tipografía va en bloque con las tablas, los guiones de
 * relleno y la fórmula de cierre (con la letra de la plantilla no hay término
 * medio coherente).
 */
export const GRUPO_TIPOGRAFIA: readonly Aspecto[] = ['tipografia', 'tablas', 'guiones', 'cierre'];

/**
 * 'mixto': manda el layout del original (mismas hojas; si no cabe, la letra
 * baja con aviso). 'reflujo': manda la letra de la plantilla (nunca se
 * reduce; el texto fluye y puede sumar hojas) sobre la hoja del original.
 */
export function motorDe(a: AspectosFormato): 'mixto' | 'reflujo' {
  return a.tipografia === 'plantilla' ? 'reflujo' : 'mixto';
}

/** Reflujo: la letra de la plantilla con todo su grupo y con su encabezado y su pie. */
export function reflujoDisponible(a: AspectosFormato): boolean {
  return [...GRUPO_TIPOGRAFIA, 'encabezado', 'pie'].every((k) => a[k as Aspecto] === 'plantilla');
}

/** Mixto: todo lo de fuera de ASPECTOS_DISPONIBLES va del original. Reflujo: reflujoDisponible. */
export function combinacionDisponible(a: AspectosFormato): boolean {
  if (motorDe(a) === 'reflujo') return reflujoDisponible(a);
  return ASPECTOS.every((k) => ASPECTOS_DISPONIBLES.includes(k) || a[k] === 'original');
}

/** La orientación va con su hoja, y la hoja con sus márgenes. */
export function incoherencia(a: AspectosFormato): 'orientacionSinHoja' | 'hojaSinMargenes' | null {
  if (a.orientacion === 'plantilla' && a.hoja !== 'plantilla') return 'orientacionSinHoja';
  if (a.hoja === 'plantilla' && a.margenes !== 'plantilla') return 'hojaSinMargenes';
  return null;
}

/** Lo que arrastra cambiar un aspecto para seguir siendo coherente. */
export function ajustarCoherencia(a: AspectosFormato, cambiado: Aspecto): AspectosFormato {
  const out: AspectosFormato = { ...a };
  if (cambiado === 'hoja' && out.hoja === 'plantilla') out.margenes = 'plantilla';
  else if (cambiado === 'hoja' && out.hoja === 'original') out.orientacion = 'original';
  else if (cambiado === 'orientacion' && out.orientacion === 'plantilla') { out.hoja = 'plantilla'; out.margenes = 'plantilla'; }
  else if (cambiado === 'margenes' && out.margenes === 'original') { out.hoja = 'original'; out.orientacion = 'original'; }
  return out;
}

export function todoOriginal(a: AspectosFormato): boolean {
  return ASPECTOS.every((k) => a[k] === 'original');
}

const enOrden = (conjunto: Set<Aspecto>): Aspecto[] => ASPECTOS.filter((a) => conjunto.has(a));

export function aspectosDeCandados(lockedFields: unknown): Aspecto[] {
  if (!Array.isArray(lockedFields)) return [];
  const s = new Set<Aspecto>();
  for (const campo of lockedFields) {
    if (typeof campo === 'string' && Object.prototype.hasOwnProperty.call(CANDADO_A_ASPECTO, campo)) s.add(CANDADO_A_ASPECTO[campo]);
  }
  return enOrden(s);
}

/** Une listas de aspectos bloqueados: únicos, en el orden de ASPECTOS; ignora lo que no sea un aspecto. */
export function unirBloqueados(...listas: unknown[]): Aspecto[] {
  const s = new Set<Aspecto>();
  for (const l of listas) {
    if (!Array.isArray(l)) continue;
    for (const x of l) if (esAspecto(x)) s.add(x);
  }
  return enOrden(s);
}

/** ¿Algún aspecto bloqueado pasaría al original con estos aspectos? */
export function chocaConCandados(a: AspectosFormato | null, bloqueados: readonly Aspecto[]): boolean {
  return !!a && bloqueados.some((b) => a[b] === 'original');
}

export function atajoPermitido(atajo: Atajo, bloqueados: readonly Aspecto[]): boolean {
  return !chocaConCandados(aspectosDeAtajo(atajo), bloqueados);
}

export type CamposBandas = {
  headerEnabled?: boolean; headerText?: string | null; headerColumns?: unknown[];
  footerEnabled?: boolean; footerText?: string | null; footerColumns?: unknown[];
  pageNumbering?: boolean; repeatSealSignature?: boolean;
};

/** ¿La plantilla saca alguna banda (encabezado, pie, número de página o sello repetido)? */
export function traeBandas(t: CamposBandas): boolean {
  const hayTexto = (s: string | null | undefined) => typeof s === 'string' && s.trim() !== '';
  const hayColumnas = (c: unknown) => Array.isArray(c) && c.length > 0;
  return !!(
    (t.headerEnabled && (hayTexto(t.headerText) || hayColumnas(t.headerColumns)))
    || (t.footerEnabled && (hayTexto(t.footerText) || hayColumnas(t.footerColumns)))
    || t.pageNumbering
    || t.repeatSealSignature
  );
}

/**
 * ¿La plantilla aporta algo con estos aspectos? La hoja o los márgenes; o un
 * lado pedido a la plantilla que la plantilla saca (`lados`, la regla del
 * aplicador). Es la regla 7 del backend: aquí solo decide si se avisa antes
 * de guardar.
 */
export function aportaAlgo(a: AspectosFormato, lados: { encabezado: boolean; pie: boolean }): boolean {
  if (a.hoja === 'plantilla' || a.margenes === 'plantilla' || a.tipografia === 'plantilla') return true;
  return (a.encabezado === 'plantilla' && lados.encabezado) || (a.pie === 'plantilla' && lados.pie);
}

/**
 * Tras guardar: 'perdida' si se pidió una mezcla y la plantilla guardada
 * vuelve sin ella (p. ej. la 032 aún no está en ese ambiente y el backend
 * guardó el resto). El formulario sigue con cambios sin guardar.
 */
export function comprobarAspectosGuardados(
  input: { aspectos?: unknown },
  guardada: { aspectos?: unknown } | null | undefined,
): 'ok' | 'perdida' {
  return input.aspectos != null && guardada?.aspectos == null ? 'perdida' : 'ok';
}
